import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserContext } from '../contexts';
import { useTelegram } from '../hooks/useTelegram';
import { api } from '../services/api';
import type { BatteryCycleStatus } from '@trading-sim/shared';

function formatTimer(totalSeconds: number): { minutes: string; seconds: string } {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return { minutes: String(m), seconds: String(s).padStart(2, '0') };
}

const TIER_LABELS: Record<number, string> = { 1: 'Tier 1', 2: 'Tier 2', 3: 'Tier 3' };
const TIER_COLORS: Record<number, string> = { 1: '#10b981', 2: '#3b82f6', 3: '#a855f7' };

export function BatteryScreen() {
  const { t } = useTranslation();
  const { updateBalance } = useUserContext();
  const { showAlert } = useTelegram();
  const [status, setStatus] = useState<BatteryCycleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [charging, setCharging] = useState(false);
  const [startingCycle, setStartingCycle] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [withdrawalCountdown, setWithdrawalCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.getBatteryStatus();
      const data: BatteryCycleStatus = response.data;
      setStatus(data);
      setCooldown(data.cooldownSeconds);
      setWithdrawalCountdown(data.withdrawal.deadlineSeconds);
    } catch (error) {
      console.error('Failed to fetch battery status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c: number) => Math.max(0, c - 1));
      setWithdrawalCountdown((c: number) => Math.max(0, c - 1));
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const handleCharge = async () => {
    if (charging || !status?.canCharge || cooldown > 0) return;
    setCharging(true);
    try {
      const response = await api.chargeBattery();
      const result = response.data;

      const nextCooldown = Math.max(0, Math.ceil((new Date(result.nextChargeAt).getTime() - Date.now()) / 1000));
      setCooldown(nextCooldown);

      setStatus((prev: BatteryCycleStatus | null) => prev ? {
        ...prev,
        chargesToday: result.chargesToday,
        health: result.health,
        healthStatus: result.health >= 80 ? 'excellent' : result.health >= 50 ? 'good' : result.health >= 25 ? 'low' : result.health >= 1 ? 'critical' : 'dead',
        todayEarnings: result.todayEarnings,
        cycleEarnings: result.cycleEarnings,
        nextChargeAt: result.nextChargeAt,
        cooldownSeconds: nextCooldown,
        canCharge: false,
        perfectCharge: {
          ...prev.perfectCharge,
          progress: result.chargesToday,
          completed: result.perfectChargeCompleted,
          eligible: prev.tier === 1 || !result.wasGapPenalty,
        },
      } : prev);

      if (result.wasGapPenalty) {
        showAlert(`⚠️ Gap penalty: +${result.reward.toLocaleString()} WiTon (10%)`);
      } else if (result.perfectChargeCompleted) {
        showAlert(t('battery.perfect_bonus_alert', { reward: result.perfectChargeBonus.toLocaleString() }));
      }
    } catch (error: any) {
      showAlert(`${error.response?.data?.error?.message || 'Error'}`);
    } finally {
      setCharging(false);
    }
  };

  const handleWithdraw = async () => {
    if (withdrawing) return;
    setWithdrawing(true);
    try {
      const response = await api.withdrawBatteryEarnings();
      const result = response.data;
      updateBalance(result.newBalance);
      showAlert(t('battery.withdraw_success', { amount: result.amount.toLocaleString() }));
      await fetchStatus();
    } catch (error: any) {
      showAlert(`${error.response?.data?.error?.message || 'Error'}`);
    } finally {
      setWithdrawing(false);
    }
  };

  const handleUpgrade = async () => {
    if (upgrading || !status?.upgrade.available) return;
    setUpgrading(true);
    try {
      const response = await api.upgradeBattery();
      const result = response.data;
      if (result.remainderClaimed > 0) {
        updateBalance(result.remainderClaimed);
      }
      showAlert(`Upgraded to Tier ${result.newTier}! Cost: ${result.upgradeCost.toLocaleString()} WiTon${result.remainderClaimed > 0 ? `, +${result.remainderClaimed.toLocaleString()} WiTon claimed` : ''}`);
      await fetchStatus();
    } catch (error: any) {
      showAlert(`${error.response?.data?.error?.message || 'Error'}`);
    } finally {
      setUpgrading(false);
    }
  };

  const handleStartCycle = async (tier: number) => {
    if (startingCycle) return;
    setStartingCycle(true);
    try {
      await api.startBatteryCycle(tier);
      await fetchStatus();
    } catch (error: any) {
      showAlert(`${error.response?.data?.error?.message || 'Error'}`);
    } finally {
      setStartingCycle(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div>{t('common.loading')}</div>
      </div>
    );
  }

  const needsPurchase = !status || status.status === 'none' || status.status === 'burned' || status.status === 'completed' || status.status === 'expired';
  const isPendingPayment = status?.status === 'pending_payment';

  const handleCopyMemo = (text: string) => {
    navigator.clipboard.writeText(text).then(() => showAlert(t('deposit.copied')));
  };

  if (needsPurchase || isPendingPayment) {
    return (
      <div style={styles.pageContainer}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>{t('battery.title')}</h1>
        </div>
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <div style={styles.purchaseCard}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔋</div>

            {isPendingPayment && status?.payment?.memo ? (
              <>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', margin: '0 0 16px' }}>
                  {t('battery.payment_instructions')}
                </p>
                <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: 700, marginBottom: '12px' }}>
                  {status.purchasePrice} USDT
                </div>
                <div style={{ textAlign: 'left', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>{t('battery.wallet_label')}</div>
                  <div style={{ fontSize: '12px', wordBreak: 'break-all', color: '#fff', marginBottom: '8px' }}>{status.payment.walletAddress}</div>
                  <button onClick={() => handleCopyMemo(status.payment.walletAddress)} style={styles.copyBtn}>{t('deposit.copy')}</button>
                </div>
                <div style={{ textAlign: 'left', background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Memo</div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#10b981', marginBottom: '8px' }}>{status.payment.memo}</div>
                  <button onClick={() => handleCopyMemo(status.payment.memo!)} style={styles.copyBtn}>{t('deposit.copy')}</button>
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', padding: '10px', background: 'rgba(16,185,129,0.1)', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.2)' }}>
                  {t('battery.payment_auto_activate')}
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left', marginBottom: '16px' }}>
                  {[
                    { tier: 1, price: 30, reward: 300, max: 24, days: 30, lucky: '30K', color: '#10b981' },
                    { tier: 2, price: 90, reward: 1000, max: 12, days: 90, lucky: '90K', color: '#3b82f6' },
                    { tier: 3, price: 300, reward: 2000, max: 12, days: 300, lucky: '300K', color: '#a855f7' },
                  ].map((t) => (
                    <div key={t.tier} style={{
                      background: 'rgba(0,0,0,0.3)', borderRadius: '14px', padding: '16px',
                      border: `1px solid ${t.color}33`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div>
                          <span style={{ fontSize: '16px', fontWeight: 800, color: t.color }}>Tier {t.tier}</span>
                          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginLeft: '8px' }}>{t.price} USDT</span>
                        </div>
                        <span style={{ fontSize: '11px', color: t.color, padding: '2px 8px', borderRadius: '10px', background: `${t.color}18` }}>
                          {t.days} days
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                        <span>+{t.reward.toLocaleString()} / charge</span>
                        <span>{t.max}x / day</span>
                        <span>Perfect: 12K</span>
                        <span>Lucky: {t.lucky}</span>
                      </div>
                      <button
                        onClick={() => handleStartCycle(t.tier)}
                        disabled={startingCycle}
                        style={{
                          width: '100%', padding: '12px',
                          background: `linear-gradient(135deg, ${t.color} 0%, ${t.color}cc 100%)`,
                          border: 'none', borderRadius: '10px', color: '#fff',
                          fontSize: '14px', fontWeight: 700,
                          opacity: startingCycle ? 0.5 : 1,
                          cursor: startingCycle ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {startingCycle ? '...' : `Buy — ${t.price} USDT`}
                      </button>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', padding: '10px', background: 'rgba(245,158,11,0.1)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.2)' }}>
                  ⚠️ {t('battery.withdrawal_warning')}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const tier = status.tier;
  const tierColor = TIER_COLORS[tier] || '#10b981';
  const tierLabel = TIER_LABELS[tier] || `Tier ${tier}`;
  const healthPercent = status.health;
  const healthColor = healthPercent >= 80 ? '#10b981' : healthPercent >= 50 ? '#f59e0b' : healthPercent >= 25 ? '#f97316' : '#ef4444';
  const cycleDays = status.luckyCharge.target;
  const cycleProgress = status.cycleDayNumber / cycleDays;
  const isOnCooldown = cooldown > 0;
  const chargeDisabled = !status.canCharge || charging || isOnCooldown;
  const isAwaitingWithdrawal = status.status === 'awaiting_withdrawal';
  const timer = formatTimer(cooldown);

  return (
    <div style={styles.pageContainer}>
      {/* Header with Tier */}
      <div style={{ padding: '16px 20px 8px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>
          {t('battery.title')}
          <span style={{
            marginLeft: '10px', fontSize: '13px', fontWeight: 700,
            padding: '3px 10px', borderRadius: '20px',
            background: `${tierColor}22`, color: tierColor,
            border: `1px solid ${tierColor}44`,
          }}>
            {tierLabel}
          </span>
        </h1>
      </div>

      {/* Withdrawal + Upgrade Banner */}
      {isAwaitingWithdrawal && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(239,68,68,0.2) 0%, rgba(245,158,11,0.2) 100%)',
            borderRadius: '14px', padding: '14px', textAlign: 'center',
            border: '1px solid rgba(239,68,68,0.3)',
          }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#ef4444', marginBottom: '4px' }}>
              {t('battery.withdrawal_must')}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '10px' }}>
              {t('battery.withdrawal_deadline')}: {formatTimer(withdrawalCountdown).minutes}m {formatTimer(withdrawalCountdown).seconds}s
            </div>

            {/* Withdraw button */}
            {!status.withdrawal.isWithdrawn && (
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                style={{
                  width: '100%', padding: '12px', marginBottom: '8px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none', borderRadius: '10px', color: '#fff',
                  fontSize: '14px', fontWeight: 700,
                  cursor: withdrawing ? 'not-allowed' : 'pointer',
                  opacity: withdrawing ? 0.5 : 1,
                }}
              >
                {withdrawing ? t('common.loading') : t('battery.withdraw_button', { amount: status.cycleEarnings.toLocaleString() })}
              </button>
            )}
            {status.withdrawal.isWithdrawn && (
              <div style={{ fontSize: '13px', color: '#10b981', fontWeight: 600, marginBottom: '8px' }}>
                {t('battery.withdrawal_done')}
              </div>
            )}

            {/* Upgrade button */}
            {status.upgrade.available && !status.withdrawal.isWithdrawn && (
              <div style={{ marginTop: '4px' }}>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
                  — or —
                </div>
                <button
                  onClick={handleUpgrade}
                  disabled={upgrading || !status.upgrade.canAfford}
                  style={{
                    width: '100%', padding: '12px',
                    background: status.upgrade.canAfford
                      ? `linear-gradient(135deg, ${TIER_COLORS[status.upgrade.nextTier!]} 0%, ${TIER_COLORS[status.upgrade.nextTier!]}cc 100%)`
                      : 'rgba(255,255,255,0.1)',
                    border: 'none', borderRadius: '10px', color: '#fff',
                    fontSize: '14px', fontWeight: 700,
                    cursor: upgrading || !status.upgrade.canAfford ? 'not-allowed' : 'pointer',
                    opacity: upgrading || !status.upgrade.canAfford ? 0.5 : 1,
                  }}
                >
                  {upgrading ? t('common.loading')
                    : `Upgrade to Tier ${status.upgrade.nextTier} (${status.upgrade.upgradeCost.toLocaleString()} WiTon)`}
                </button>
                {!status.upgrade.canAfford && (
                  <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                    Need {status.upgrade.upgradeCost.toLocaleString()} WiTon (have {status.cycleEarnings.toLocaleString()})
                  </div>
                )}
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' }}>
                  Remainder will be claimed to main balance + referral bonus
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Battery Health Card */}
      <div style={{ padding: '0 16px 12px' }}>
        <div style={styles.card}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: '80px', height: '100px', flexShrink: 0 }}>
              <svg viewBox="0 0 60 90" width="80" height="100">
                <rect x="15" y="0" width="30" height="6" rx="2" fill="rgba(255,255,255,0.3)" />
                <rect x="4" y="6" width="52" height="80" rx="8" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                <defs>
                  <clipPath id="batteryClip">
                    <rect x="7" y={9 + 74 * (1 - healthPercent / 100)} width="46" height={74 * (healthPercent / 100)} rx="5" />
                  </clipPath>
                </defs>
                <rect x="7" y="9" width="46" height="74" rx="5" fill={healthColor} opacity="0.2" />
                <rect x="7" y="9" width="46" height="74" rx="5" fill={healthColor} clipPath="url(#batteryClip)" />
                <text x="30" y="54" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">⚡</text>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>{t('battery.health_label')}</div>
              <div style={{ fontSize: '36px', fontWeight: 800, color: healthColor, lineHeight: 1 }}>{healthPercent}%</div>
              <div style={{ fontSize: '13px', color: healthColor, marginTop: '4px' }}>{t(`battery.health_${status.healthStatus}`)}</div>
            </div>
            <div style={styles.cycleBox}>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>{t('battery.cycle_remaining')}</div>
              <div style={{ fontSize: '24px', fontWeight: 800 }}>{status.cycleDaysRemaining}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>{t('battery.cycle_remaining_days', { days: '' }).trim()}</div>
              <div style={{ marginTop: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
                <div style={{ width: `${cycleProgress * 100}%`, height: '100%', background: tierColor, borderRadius: '4px', transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                {t('battery.cycle_progress', { current: status.cycleDayNumber, total: cycleDays })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Section */}
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { label: t('battery.balance_witon'), value: status.cycleEarnings.toLocaleString(), sub: 'WiTon', color: '#fff' },
            { label: t('battery.today_earnings'), value: status.todayEarnings.toLocaleString(), sub: 'WiTon', color: '#10b981' },
            { label: t('battery.cycle_earnings'), value: status.cycleEarnings.toLocaleString(), sub: 'WiTon', color: '#10b981' },
          ].map((item, i) => (
            <div key={i} style={styles.balanceItem}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{item.label}</div>
              <div style={{ fontSize: '20px', fontWeight: 800, marginTop: '4px', color: item.color }}>{item.value}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{item.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Charge Button + Cooldown */}
      {!isAwaitingWithdrawal && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <button
              onClick={handleCharge}
              disabled={chargeDisabled}
              style={{
                ...styles.chargeButton,
                background: chargeDisabled ? 'rgba(255,255,255,0.1)' : `linear-gradient(135deg, ${tierColor} 0%, ${tierColor}cc 100%)`,
                opacity: chargeDisabled ? 0.5 : 1,
                cursor: chargeDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {isOnCooldown ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' }}>{t('battery.cooldown')}</div>
                  <div style={{ fontSize: '22px', fontWeight: 800 }}>{timer.minutes}:{timer.seconds}</div>
                </div>
              ) : (
                <>
                  <span style={{ fontSize: '22px' }}>⚡</span>
                  <span>
                    {charging ? t('battery.charging') : t('battery.charge_button')}
                    {!charging && <><br /><span style={{ fontSize: '14px', opacity: 0.9 }}>{t('battery.charge_reward', { reward: status.chargeReward.toLocaleString() })}</span></>}
                  </span>
                </>
              )}
            </button>
            <div style={styles.chargesCounter}>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{t('battery.charges_today')}</div>
              <div style={{ fontSize: '24px', fontWeight: 800, marginTop: '4px' }}>
                <span style={{ color: tierColor }}>{status.chargesToday}</span>
                <span style={{ color: 'rgba(255,255,255,0.4)' }}> / {status.maxChargesPerDay}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gap Penalty Info for Tiers 2/3 */}
      {tier >= 2 && !isAwaitingWithdrawal && (
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{
            background: !status.perfectCharge.eligible ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
            borderRadius: '10px', padding: '10px 14px',
            border: `1px solid ${!status.perfectCharge.eligible ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'}`,
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span style={{ fontSize: '16px' }}>{!status.perfectCharge.eligible ? '⚠️' : 'ℹ️'}</span>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>
              {!status.perfectCharge.eligible
                ? 'Gap penalty active today — charge was reduced to 10%. No Perfect Charge bonus today.'
                : `Keep charging within ${tier === 3 ? '9' : '8'}h gaps to earn full rewards and Perfect Charge bonus.`}
            </div>
          </div>
        </div>
      )}

      {/* Bonus Cards */}
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Perfect Charge */}
          <div style={{ ...styles.bonusCard, border: `1px solid ${tierColor}25` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{ ...styles.bonusIcon, background: `linear-gradient(135deg, ${tierColor} 0%, ${tierColor}cc 100%)` }}>⭐</div>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>{t('battery.perfect_charge_title')}</div>
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '10px', lineHeight: 1.4 }}>
              {t('battery.perfect_charge_desc', { target: status.perfectCharge.target })}
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: tierColor, marginBottom: '10px' }}>
              +{status.perfectCharge.reward.toLocaleString()} WiTon
            </div>
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '4px', height: '8px', overflow: 'hidden', marginBottom: '4px' }}>
              <div style={{ width: `${(status.perfectCharge.progress / status.perfectCharge.target) * 100}%`, height: '100%', background: `linear-gradient(90deg, ${tierColor}, ${tierColor}cc)`, borderRadius: '4px', transition: 'width 0.3s' }} />
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>
              {status.perfectCharge.progress} / {status.perfectCharge.target}
              {tier >= 2 && !status.perfectCharge.eligible && (
                <span style={{ color: '#ef4444', marginLeft: '6px' }}>(gap penalty)</span>
              )}
            </div>
          </div>

          {/* Lucky Charge */}
          <div style={{ ...styles.bonusCard, border: '1px solid rgba(245, 158, 11, 0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{ ...styles.bonusIcon, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>🎁</div>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>{t('battery.lucky_charge_title')}</div>
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginBottom: '10px', lineHeight: 1.4 }}>
              {t('battery.lucky_charge_desc', { target: cycleDays })}
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#f59e0b', marginBottom: '10px' }}>
              +{status.luckyCharge.reward.toLocaleString()} WiTon
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>
              {status.luckyCharge.activeDaysBitmap.slice(0, Math.min(cycleDays, 30)).map((active: boolean, i: number) => (
                <div key={i} style={{
                  width: cycleDays > 90 ? '8px' : '14px',
                  height: cycleDays > 90 ? '8px' : '14px',
                  borderRadius: '3px',
                  background: active ? '#10b981' : 'rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: cycleDays > 90 ? '5px' : '8px',
                }}>
                  {active && cycleDays <= 90 ? '✓' : ''}
                </div>
              ))}
              {cycleDays > 30 && (
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginLeft: '4px', display: 'flex', alignItems: 'center' }}>
                  +{cycleDays - 30} more
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>
              {status.luckyCharge.activeDays} / {cycleDays}
            </div>
          </div>
        </div>
      </div>

      {/* Rules Section */}
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ ...styles.card, padding: '16px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>{t('battery.rules_title')}</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[
              ...(tier >= 2 ? [{ label: `${tier === 3 ? '9' : '8'}h gap`, desc: 'Charge reduced to 10%, no perfect bonus', icon: '⏰', color: '#3b82f6' }] : []),
              { label: t('battery.rule_12h_label'), desc: t('battery.rule_12h'), icon: '🕐', color: '#f59e0b' },
              { label: t('battery.rule_24h_label'), desc: t('battery.rule_24h'), icon: '🕐', color: '#f97316' },
              { label: t('battery.rule_48h_label'), desc: t('battery.rule_48h'), icon: '🔥', color: '#ef4444' },
            ].map((rule, i) => (
              <div key={i} style={styles.ruleItem}>
                <div style={{ fontSize: '18px', marginBottom: '4px' }}>{rule.icon}</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: rule.color }}>{rule.label}</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '2px', lineHeight: 1.3 }}>{rule.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Withdrawal Warning */}
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '8px', background: 'rgba(245,158,11,0.08)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.15)' }}>
          ⚠️ {t('battery.withdrawal_warning')}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingContainer: {
    minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  pageContainer: {
    minHeight: '100vh', background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
    color: '#fff', paddingBottom: '80px',
  },
  purchaseCard: {
    background: 'rgba(10, 22, 40, 0.6)', borderRadius: '20px', padding: '40px 20px',
    border: '1px solid rgba(16, 185, 129, 0.2)',
  },
  copyBtn: {
    padding: '6px 16px', background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.3)',
    borderRadius: '8px', color: '#10b981', fontSize: '12px', cursor: 'pointer',
  },
  purchaseButton: {
    width: '100%', padding: '16px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    border: 'none', borderRadius: '14px', color: '#fff', fontSize: '16px', fontWeight: 700,
  },
  card: {
    background: 'rgba(10, 22, 40, 0.6)', borderRadius: '20px', padding: '20px',
    border: '1px solid rgba(16, 185, 129, 0.15)',
  },
  cycleBox: {
    background: 'rgba(0,0,0,0.3)', borderRadius: '14px', padding: '12px 16px',
    textAlign: 'center' as const, border: '1px solid rgba(255,255,255,0.1)',
  },
  balanceItem: {
    flex: 1, background: 'rgba(10, 22, 40, 0.6)', borderRadius: '14px', padding: '14px',
    border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' as const,
  },
  chargeButton: {
    flex: 1, padding: '18px', border: 'none', borderRadius: '16px', color: '#fff',
    fontSize: '17px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  },
  chargesCounter: {
    background: 'rgba(10, 22, 40, 0.6)', borderRadius: '16px', padding: '14px 18px',
    border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' as const,
    alignItems: 'center', justifyContent: 'center', minWidth: '100px',
  },
  bonusCard: {
    flex: 1, background: 'rgba(10, 22, 40, 0.6)', borderRadius: '16px', padding: '16px',
  },
  bonusIcon: {
    width: '32px', height: '32px', borderRadius: '8px', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: '16px',
  },
  ruleItem: {
    flex: 1, textAlign: 'center' as const, padding: '10px 4px',
    background: 'rgba(0,0,0,0.2)', borderRadius: '10px',
  },
};
